import { useEffect } from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'
import type { ErrorEvent } from '@sentry/react'
import { isStaleAppBundleLoadError } from './chunkLoadError'
import type {
  CaptureCheckoutIssueInput,
  SentryCheckoutContext,
  SentrySalonContextInput,
} from './sentryCheckout.types'

const EMAIL_PATTERN = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g
const PHONE_PATTERN = /\b(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,4}\)?[-.\s]?)?\d{3}[-.\s]?\d{4,}\b/g
const CHECKOUT_ROUTE_PATTERN = /\/betalen|\/bevestiging/
const CHECKOUT_MESSAGE_MAX = 200

/** Checkout performance span names (Sentry Performance). */
export const CHECKOUT_SPAN = {
  loadContext: 'checkout.load_checkout_context',
  returnStatus: 'checkout.return_status',
  createPayment: 'checkout.create_payment',
  mollieRedirect: 'checkout.mollie_redirect',
} as const

/** Klanten-pagina performance spans (lazy detail, Fase A). */
export const KLANTEN_SPAN = {
  loadLightList: 'klanten.load_light_list',
  loadDetail: 'klanten.load_detail',
  enrichServices: 'klanten.enrich_services',
  crmLookup: 'klanten.crm_lookup',
} as const

export async function withCheckoutSpan<T> (
  spanName: string,
  fn: () => T | Promise<T>,
): Promise<T> {
  if (!isSentryEnabled()) {
    return await Promise.resolve(fn())
  }
  return Sentry.startSpan({ name: spanName, op: 'checkout' }, () => fn())
}

export async function withKlantenSpan<T> (
  spanName: string,
  fn: () => T | Promise<T>,
): Promise<T> {
  if (!isSentryEnabled()) {
    return await Promise.resolve(fn())
  }
  return Sentry.startSpan({ name: spanName, op: 'klanten' }, () => fn())
}

let initAttempted = false
let initSucceeded = false

export interface SentryEnvInput {
  dsn?: string
  dev: boolean
  debug?: string
}

export type { CaptureCheckoutIssueInput, CheckoutIssueCode } from './sentryCheckout.types'

/** Pure helper — testbaar zonder SDK-init. */
export function shouldEnableSentry (env: SentryEnvInput): boolean {
  if (!env.dsn?.trim()) return false
  if (env.dev && env.debug !== '1') return false
  return true
}

export function getSentryRelease (): string | undefined {
  const sha = import.meta.env.VERCEL_GIT_COMMIT_SHA
  if (typeof sha === 'string' && sha.trim()) return sha.trim()
  return undefined
}

export function scrubStringForSentry (value: string): string {
  return value
    .replace(EMAIL_PATTERN, '[email]')
    .replace(PHONE_PATTERN, '[phone]')
    .replace(/payment_token=[^&\s]+/gi, 'payment_token=[redacted]')
}

function scrubValue (value: unknown): unknown {
  if (typeof value === 'string') return scrubStringForSentry(value)
  if (Array.isArray(value)) return value.map(scrubValue)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, scrubValue(v)]),
    )
  }
  return value
}

/** PII scrub voor Sentry events — export voor tests. */
export function scrubSentryEvent (event: ErrorEvent): ErrorEvent | null {
  if (event.exception?.values?.some((v) => isStaleAppBundleLoadError(new Error(v.value ?? '')))) {
    return null
  }
  if (event.message) event.message = scrubStringForSentry(event.message)
  if (event.request?.url) event.request.url = scrubStringForSentry(event.request.url)
  if (event.extra) event.extra = scrubValue(event.extra) as ErrorEvent['extra']
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => ({
      ...crumb,
      message: crumb.message ? scrubStringForSentry(crumb.message) : crumb.message,
      data: crumb.data ? (scrubValue(crumb.data) as Record<string, unknown>) : crumb.data,
    }))
  }
  return event
}

export function truncateCheckoutMessage (message: string, max = CHECKOUT_MESSAGE_MAX): string {
  const scrubbed = scrubStringForSentry(message)
  if (scrubbed.length <= max) return scrubbed
  return `${scrubbed.slice(0, max)}…`
}

export function isCheckoutRouteSampled (routeOrTransactionName: string): boolean {
  return CHECKOUT_ROUTE_PATTERN.test(routeOrTransactionName)
}

export function isCheckoutReplayPath (pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || pathname
  return /\/salon\/[^/]+\/(betalen|bevestiging)$/.test(normalized)
}

export function startCheckoutReplayBuffer (): void {
  if (!isSentryEnabled()) return
  Sentry.getReplay()?.startBuffering()
}

export async function stopCheckoutReplayDiscard (): Promise<void> {
  if (!isSentryEnabled()) return
  const replay = Sentry.getReplay()
  if (!replay) return
  await replay.stop()
}

export function flushCheckoutReplayIfActive (): void {
  if (!isSentryEnabled()) return
  const replay = Sentry.getReplay()
  if (!replay?.getReplayId()) return
  void replay.flush()
}

export function isSentryEnabled (): boolean {
  if (!initSucceeded) return false
  return shouldEnableSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    dev: import.meta.env.DEV,
    debug: import.meta.env.VITE_SENTRY_DEBUG,
  })
}

export function setSentryRouteTag (pathname: string): void {
  if (!isSentryEnabled()) return
  Sentry.setTag('route', pathname)
}

export function setSentrySalonContext ({ salonId, slug }: SentrySalonContextInput): void {
  if (!isSentryEnabled()) return
  Sentry.setTag('salon_id', salonId)
  if (slug) Sentry.setTag('salon_slug', slug)
  Sentry.setContext('salon', { salon_id: salonId, salon_slug: slug ?? undefined })
}

export function clearSentrySalonContext (): void {
  if (!isSentryEnabled()) return
  Sentry.setTag('salon_id', undefined)
  Sentry.setTag('salon_slug', undefined)
  Sentry.setContext('salon', null)
}

export function setSentryCheckoutContext (ctx: SentryCheckoutContext): void {
  if (!isSentryEnabled()) return
  if (ctx.bookingNumber != null) {
    Sentry.setTag('booking_number', String(ctx.bookingNumber))
  }
  if (ctx.intentId) Sentry.setTag('intent_id', ctx.intentId)
  if (ctx.bookingId) Sentry.setTag('booking_id', ctx.bookingId)
  Sentry.setContext('checkout', {
    booking_number: ctx.bookingNumber ?? undefined,
    intent_id: ctx.intentId ?? undefined,
    booking_id: ctx.bookingId ?? undefined,
  })
}

export function captureCheckoutIssue (input: CaptureCheckoutIssueInput): void {
  if (!isSentryEnabled()) return

  const message = truncateCheckoutMessage(input.message)

  Sentry.withScope((scope) => {
    scope.setTag('scope', 'checkout')
    scope.setTag('checkout_error_code', input.code)
    if (input.salonId) scope.setTag('salon_id', input.salonId)
    if (input.slug) scope.setTag('salon_slug', input.slug)
    if (input.phase) scope.setTag('checkout_phase', input.phase)
    if (input.bookingNumber != null) {
      scope.setTag('booking_number', String(input.bookingNumber))
    }
    if (input.intentId) scope.setTag('intent_id', input.intentId)
    if (input.bookingId) scope.setTag('booking_id', input.bookingId)
    scope.setFingerprint([input.code])
    scope.setContext('checkout_issue', {
      code: input.code,
      phase: input.phase,
      intent_id: input.intentId ?? undefined,
      booking_id: input.bookingId ?? undefined,
      booking_number: input.bookingNumber ?? undefined,
    })
    Sentry.captureMessage(message, { level: input.level ?? 'warning' })
  })

  flushCheckoutReplayIfActive()
}

export function initSentry (): void {
  if (initAttempted) return
  initAttempted = true

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!shouldEnableSentry({
    dsn,
    dev: import.meta.env.DEV,
    debug: import.meta.env.VITE_SENTRY_DEBUG,
  })) {
    return
  }

  Sentry.init({
    dsn: dsn!.trim(),
    environment: import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE,
    release: getSentryRelease(),
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampler: (ctx) => {
      const name = ctx.name ?? ''
      if (isCheckoutRouteSampled(name)) return 1.0
      const routeAttr = ctx.attributes?.['sentry.source']
      if (typeof routeAttr === 'string' && isCheckoutRouteSampled(routeAttr)) return 1.0
      return 0.05
    },
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend: scrubSentryEvent,
    ignoreErrors: [
      'ChunkLoadError',
      /Failed to fetch dynamically imported module/i,
      /Importing a module script failed/i,
      /error loading dynamically imported module/i,
    ],
    initialScope: (scope) => {
      scope.setTag('app_version', getSentryRelease() ?? 'unknown')
      scope.setTag('environment', import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE)
      return scope
    },
  })

  initSucceeded = true
}

export function captureSentryException (
  error: unknown,
  context?: Parameters<typeof Sentry.captureException>[1],
): void {
  if (!isSentryEnabled()) return
  Sentry.captureException(error, context)
  flushCheckoutReplayIfActive()
}
